import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Save, X, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { UploadFile } from '@/api/integrations';
import { Document as DocumentEntity } from '@/api/entities';
import { useAuth } from '@/hooks/useAuth';

const DOCUMENT_CATEGORIES = [
  'Marketing Materials',
  'Application Forms',
  'Program Information',
  'Agreements & Contracts',
  'Training Materials',
  'Policies & Procedures',
  'Financial Information',
  'Other'
];

export default function AddDocumentModal({ isOpen, onClose, onDocumentAdded }) {
  const { profile, user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    version: '1.0'
  });
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error'

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-fill title if empty
      if (!formData.title) {
        const fileName = selectedFile.name.replace(/\.[^/.]+$/, ""); // Remove extension
        setFormData(prev => ({ ...prev, title: fileName }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !formData.title) return;

    setIsUploading(true);
    setUploadStatus(null);

    try {
      // Upload file first
      const { file_url } = await UploadFile({ file });

      // Create document record
      const documentData = {
        title: formData.title,
        description: formData.description,
        category: formData.category || 'Other',
        version: formData.version,
        file_url: file_url,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        university_id: profile?.university_id,
        uploaded_by: user?.id,
        is_public: false
      };

      await DocumentEntity.create(documentData);
      
      setUploadStatus('success');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        category: '',
        version: '1.0'
      });
      setFile(null);
      
      // Notify parent component
      if (onDocumentAdded) {
        onDocumentAdded();
      }
      
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadStatus('error');
    }
    
    setIsUploading(false);
  };

  const handleClose = () => {
    if (!isUploading) {
      setUploadStatus(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        version: '1.0'
      });
      setFile(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Add New Document
          </DialogTitle>
        </DialogHeader>
        
        {uploadStatus === 'success' ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <h3 className="font-bold text-lg">Document Added Successfully!</h3>
            <p className="text-muted-foreground">The document has been uploaded and is now available in the library.</p>
          </div>
        ) : uploadStatus === 'error' ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="font-bold text-lg">Upload Failed</h3>
            <p className="text-muted-foreground">There was an error uploading the document. Please try again.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="document-file">Select File *</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                <input
                  id="document-file"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                />
                <label htmlFor="document-file" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    {file ? file.name : 'Click to select a file or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, DOC, XLS, PPT, TXT, Images (Max 10MB)
                  </p>
                </label>
              </div>
            </div>

            {/* Document Details */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="doc-title">Document Title *</Label>
                <Input
                  id="doc-title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter document title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="doc-description">Description</Label>
                <Textarea
                  id="doc-description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of the document"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="doc-category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_CATEGORIES.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doc-version">Version</Label>
                  <Input
                    id="doc-version"
                    value={formData.version}
                    onChange={(e) => handleInputChange('version', e.target.value)}
                    placeholder="1.0"
                  />
                </div>
              </div>
            </div>
          </form>
        )}
        
        <DialogFooter>
          {uploadStatus ? (
            <Button onClick={handleClose} className="w-full">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!file || !formData.title || isUploading}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isUploading ? 'Uploading...' : 'Add Document'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}